import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import InlineCapture from '../components/common/InlineCapture';
import AppStoreLink from '../components/common/AppStoreLink';
import { WEB_BOOKING_ENABLED } from '../config/env';
import '../styles/route.css';

/**
 * STUB — NOT WIRED. Nothing is sent and no number is stored.
 *
 * TODO: send the download link by SMS. This needs a real endpoint: the existing
 * `submitPublicContactInquiry` is an email contact form (name + email + subject
 * + message are all required) and cannot carry a phone-only lead.
 *
 * Until that endpoint exists this promises a text message that never arrives,
 * so the capture below is hidden behind SMS_CAPTURE_ENABLED. Flip it on in the
 * same change that wires this function — not before.
 */
const SMS_CAPTURE_ENABLED = false;

const sendDownloadLinkBySms = async (phone: string): Promise<string> => {
  throw new Error(`SMS delivery is not wired yet (would have texted ${phone}).`);
};

/**
 * Web booking is not live. This page exists to say so plainly and hand the
 * reader to the app, which is where booking and tracking actually happen.
 *
 * It carries the site chrome like every other public page — it was previously a
 * bare panel with no header, footer or <main> landmark.
 *
 * Both exits must be things the reader can actually do. Installing is one.
 * Pricing a route is the other: the homepage estimate takes real addresses and
 * answers from the fare-estimate endpoint, so it is a genuine capability rather
 * than a "back to home" link dressed up as a choice. This mirrors how the app
 * handles its own blocked states — no truck available, or a route past the
 * instant-trip limit — where every dead end carries two labelled ways forward.
 */
const ScheduleBookingPage: React.FC = () => {
  /* This page exists only to say that web booking is not live. Once it is, the
     page is a lie, so it forwards to the customer area instead of being edited
     into something it was never meant to be. Old links keep working. */
  if (WEB_BOOKING_ENABLED) {
    return <Navigate replace to="/account" />;
  }

  return (
    <div className="landing-shell">
      <Header />
      <main>
        <section className="sw-section sw-notice">
          <div className="container sw-notice-inner">
            <span className="sw-eyebrow">Web booking · coming soon</span>
            <h1>Booking on the web is on the way.</h1>
            <p className="sw-notice-copy">
              Your account is active, but web scheduling isn&apos;t live yet. Get the
              ShiftWagon app to book and track deliveries now — or price a route here
              first.
            </p>

            <div className="sw-hero-actions">
              <AppStoreLink placement="schedule_booking" />
              <Link className="sw-link-action" to="/#estimate">
                Price a route &rarr;
              </Link>
            </div>

            {/* Desktop visitors can't install from here — the number lets them
                pick it up on the phone that will actually run the app. */}
            {SMS_CAPTURE_ENABLED ? (
              <InlineCapture
                buttonLabel="SMS me the link"
                label="Mobile number for the download link"
                onSubmit={sendDownloadLinkBySms}
                placeholder="072 000 0000"
                showLabel
                type="tel"
              />
            ) : null}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ScheduleBookingPage;
